import type { Matter, MatterInsurance } from "@/lib/types";
import {
  formatCurrency,
  getClientFirstPartyAutoInsurances,
  getInsuranceCarrierName,
  getInvestigationCoverageStatusBadgeClass,
  getPendingDemandThirdPartyInsurances,
  getPrimaryThirdPartyInsurance,
  type InvestigationCoverageStatusTone,
} from "../matter-overview-utils";

export type PendingDemandCoverageLimitLine = {
  key: string;
  limitLabel: string;
  compactLimit: string;
  fullLimit: string;
};

export type PendingDemandCoverageGroup = {
  key: string;
  carrier: string;
  tier: "1st Party" | "3rd Party";
  limits: PendingDemandCoverageLimitLine[];
  coverageStatus: string;
  coverageStatusTone: InvestigationCoverageStatusTone;
  liabilityStatus: string;
  liabilityStatusTone: InvestigationCoverageStatusTone;
};

function formatLimitPair(
  perPerson?: number | null,
  perIncident?: number | null,
) {
  if (perPerson != null && perIncident != null) {
    return `${formatCompactLimit(perPerson)} / ${formatCompactLimit(perIncident)}`;
  }

  return formatCompactLimit(perPerson ?? perIncident);
}

function formatCompactLimit(value?: number | null) {
  if (value == null || Number.isNaN(value)) {
    return "—";
  }

  if (Math.abs(value) >= 1000) {
    const compactValue = value / 1000;
    const formatted =
      compactValue % 1 === 0
        ? compactValue.toFixed(0)
        : compactValue.toFixed(1).replace(/\.0$/, "");

    return `$${formatted}k`;
  }

  return formatCurrency(value);
}

function formatFullLimitPair(
  perPerson?: number | null,
  perIncident?: number | null,
) {
  if (perPerson != null && perIncident != null) {
    return `${formatCurrency(perPerson)} / ${formatCurrency(perIncident)}`;
  }

  return formatCurrency(perPerson ?? perIncident);
}

function getPropertyDamageLimitAmount(matter: Matter) {
  const amounts = matter.propertyDamages
    .map((damage) => damage.originalAmount)
    .filter(
      (amount): amount is number => amount != null && !Number.isNaN(amount),
    );

  if (amounts.length === 0) {
    return null;
  }

  return Math.max(...amounts);
}

function getCoverageStatusTone(
  status?: MatterInsurance["coverage"],
): InvestigationCoverageStatusTone {
  switch (status) {
    case "Confirmed":
      return "green";
    case "Denied":
      return "red";
    case "Pending":
      return "amber";
    case "Unconfirmed":
    default:
      return "gray";
  }
}

function getLiabilityStatusTone(
  liability?: MatterInsurance["liability"],
): InvestigationCoverageStatusTone {
  switch (liability) {
    case "Accepted":
      return "green";
    case "Denied":
      return "red";
    default:
      return "gray";
  }
}

function formatCoverageStatus(insurance: MatterInsurance) {
  return insurance.coverage?.trim() || "—";
}

function formatLiabilityStatus(insurance: MatterInsurance) {
  const liability = insurance.liability?.trim();

  if (!liability) {
    return "—";
  }

  if (
    liability === "Accepted" &&
    insurance.liabilityPercentage != null &&
    insurance.liabilityPercentage < 100
  ) {
    return `${liability} (${insurance.liabilityPercentage}%)`;
  }

  return liability;
}

function buildThirdPartyLimitLines(
  matter: Matter,
  insurance: MatterInsurance,
  includePropertyDamage: boolean,
): PendingDemandCoverageLimitLine[] {
  const lines: PendingDemandCoverageLimitLine[] = [];
  const hasBiLimits =
    insurance.policyLimitPerPerson != null ||
    insurance.policyLimitPerIncident != null;

  if (hasBiLimits) {
    lines.push({
      key: `${insurance.id}-liability`,
      limitLabel: "Liability",
      compactLimit: formatLimitPair(
        insurance.policyLimitPerPerson,
        insurance.policyLimitPerIncident,
      ),
      fullLimit: formatFullLimitPair(
        insurance.policyLimitPerPerson,
        insurance.policyLimitPerIncident,
      ),
    });
  }

  if (includePropertyDamage) {
    const propertyDamageLimit = getPropertyDamageLimitAmount(matter);

    if (propertyDamageLimit != null) {
      lines.push({
        key: `${insurance.id}-pd`,
        limitLabel: "PD",
        compactLimit: formatCompactLimit(propertyDamageLimit),
        fullLimit: formatCurrency(propertyDamageLimit),
      });
    }
  }

  return lines;
}

function buildFirstPartyLimitLines(
  insurance: MatterInsurance,
): PendingDemandCoverageLimitLine[] {
  const lines: PendingDemandCoverageLimitLine[] = [];

  if (
    insurance.umExists ||
    insurance.umPerPerson != null ||
    insurance.umPerIncident != null
  ) {
    lines.push({
      key: `${insurance.id}-um`,
      limitLabel: "UM",
      compactLimit: formatLimitPair(
        insurance.umPerPerson,
        insurance.umPerIncident,
      ),
      fullLimit: formatFullLimitPair(
        insurance.umPerPerson,
        insurance.umPerIncident,
      ),
    });
  }

  if (
    insurance.uimExists ||
    insurance.uimPerPerson != null ||
    insurance.uimPerIncident != null
  ) {
    lines.push({
      key: `${insurance.id}-uim`,
      limitLabel: "UIM",
      compactLimit: formatLimitPair(
        insurance.uimPerPerson,
        insurance.uimPerIncident,
      ),
      fullLimit: formatFullLimitPair(
        insurance.uimPerPerson,
        insurance.uimPerIncident,
      ),
    });
  }

  if (insurance.medPay || insurance.medPayLimit != null) {
    lines.push({
      key: `${insurance.id}-medpay`,
      limitLabel: "MedPay",
      compactLimit: formatCompactLimit(insurance.medPayLimit),
      fullLimit: formatCurrency(insurance.medPayLimit),
    });
  }

  return lines;
}

function buildCoverageGroup(
  matter: Matter,
  insurance: MatterInsurance,
  tier: PendingDemandCoverageGroup["tier"],
  limits: PendingDemandCoverageLimitLine[],
): PendingDemandCoverageGroup {
  return {
    key: insurance.id,
    carrier: getInsuranceCarrierName(matter, insurance),
    tier,
    limits,
    coverageStatus: formatCoverageStatus(insurance),
    coverageStatusTone: getCoverageStatusTone(insurance.coverage),
    liabilityStatus: formatLiabilityStatus(insurance),
    liabilityStatusTone: getLiabilityStatusTone(insurance.liability),
  };
}

export function getPendingDemandCoverageGroups(
  matter: Matter,
  { includeExplicitTypes = false }: { includeExplicitTypes?: boolean } = {},
): PendingDemandCoverageGroup[] {
  const primaryThirdPartyInsurance = getPrimaryThirdPartyInsurance(matter);
  const thirdParty = getPendingDemandThirdPartyInsurances(matter);
  const firstParty = getClientFirstPartyAutoInsurances(matter);
  if (includeExplicitTypes) {
    for (const insurance of matter.insurances) {
      if (insurance.coverageEnabled === false) continue;
      const group =
        insurance.type === "third_party_liability"
          ? thirdParty
          : insurance.type === "first_party"
            ? firstParty
            : null;
      if (group && !group.some((item) => item.id === insurance.id))
        group.push(insurance);
    }
  }
  const thirdPartyGroups = thirdParty
    .filter(
      (insurance) =>
        !includeExplicitTypes || insurance.type.toLowerCase() !== "health",
    )
    .map((insurance) =>
      buildCoverageGroup(
        matter,
        insurance,
        "3rd Party",
        buildThirdPartyLimitLines(
          matter,
          insurance,
          // Legacy included a property-damage estimate here. The modular
          // widget shows recorded policy limits only, never damages as limits.
          !includeExplicitTypes &&
            insurance.id === primaryThirdPartyInsurance?.id,
        ),
      ),
    );
  const firstPartyGroups = firstParty.map((insurance) =>
    buildCoverageGroup(
      matter,
      insurance,
      "1st Party",
      buildFirstPartyLimitLines(insurance),
    ),
  );

  return [...thirdPartyGroups, ...firstPartyGroups];
}

export function getPendingDemandStatusBadgeClass(
  tone: InvestigationCoverageStatusTone,
) {
  return getInvestigationCoverageStatusBadgeClass(tone);
}
